
import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar,
  Stethoscope,
  PartyPopper,
  Clock,
  Zap
} from "lucide-react";
import { FormData } from './types';

interface TimeOffStepProps {
  formData: FormData;
  formErrors: Record<string, string>;
  onInputChange: (field: string, value: string, category?: 'expenses' | 'timeOff') => void;
  onNext: () => void;
  calculateWorkingHours: () => number;
}

export const TimeOffStep = ({ 
  formData, 
  formErrors, 
  onInputChange, 
  onNext,
  calculateWorkingHours 
}: TimeOffStepProps) => {
  const timeOffFields = [
    { 
      id: 'vacation', 
      label: 'How many vacation days will you take in the year?',
      icon: Calendar,
      color: 'text-accent-blue',
      placeholder: 'e.g. 15 days'
    },
    { 
      id: 'sick', 
      label: "And let's add a buffer for sick days",
      icon: Stethoscope,
      color: 'text-accent-green',
      placeholder: 'e.g. 5 days'
    },
    { 
      id: 'holidays', 
      label: "Don't forget the holidays!",
      icon: PartyPopper,
      color: 'text-accent-orange',
      placeholder: 'e.g. 10 days'
    },
  ] as const;

  const workingHours = calculateWorkingHours();
  const totalTimeOff = Object.values(formData.timeOff).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-accent-blue/20 to-accent-green/20 mb-6 animate-scale-in">
          <Clock className="w-8 h-8 text-accent-blue" />
        </div>
        <h2 className="text-3xl font-bold mb-3 gradient-text">Time is Money</h2>
        <p className="text-text-secondary text-lg leading-relaxed max-w-md mx-auto">
          Everyone needs a break. Let's factor in your well-deserved time off
        </p>
      </div>

      <div className="grid gap-6">
        {timeOffFields.map(({ id, label, icon: Icon, color, placeholder }, index) => (
          <div key={id} className="relative group animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
            <label htmlFor={id} className="block text-base font-medium text-text-primary mb-3 flex items-center gap-3">
              <Icon className={`w-5 h-5 ${color}`} />
              {label}
            </label>
            <div className="relative">
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.timeOff[id]}
                onChange={(e) => onInputChange(id, e.target.value, 'timeOff')}
                className="pr-16 py-3 text-lg bg-card-bg border-2 border-border hover:border-primary/50 focus:border-primary transition-all duration-300 rounded-xl"
                placeholder={placeholder}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary font-medium">
                days
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </div>
            {formErrors[id] && (
              <p className="text-error mt-2 text-sm flex items-center gap-1 animate-fade-in">
                <span className="w-1 h-1 bg-error rounded-full"></span>
                {formErrors[id]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Enhanced Working Hours Display */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-accent-blue/10 to-accent-green/10 rounded-2xl blur-xl"></div>
        <div className="relative p-6 bg-gradient-to-br from-card-bg to-card-hover rounded-2xl border border-primary/20 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-blue to-accent-green flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Working Hours per Year</p>
                <p className="text-2xl font-bold text-text-primary">
                  {workingHours.toLocaleString()} hours
                </p>
              </div>
            </div>
            <div className="text-right">
              <Badge 
                variant="secondary" 
                className="bg-gradient-to-r from-accent-blue/20 to-accent-green/20 text-text-primary border-accent-blue/30 mb-2"
              >
                {totalTimeOff > 0 ? `${totalTimeOff} days off` : 'Add time off above'}
              </Badge>
              <p className="text-xs text-text-secondary">
                {Math.round((totalTimeOff / 365) * 100)}% of year as time off
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-6">
        <Button
          onClick={onNext}
          className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-xl"
        >
          Next: The Big Reveal
          <span className="ml-2">🎉</span>
        </Button>
      </div>
    </div>
  );
};
